// @ts-check
// Barra flutuante do celular (doc 07, seção 13.4; contrato 08-contrato-navegacao-mobile.md, seção 4): menu (abre a
// gaveta), buscar, conversas e visitas, conforme o papel. Some enquanto a gaveta ou o painel lateral estão abertos e ao
// rolar para baixo; volta ao rolar para cima.
import { html } from 'htm/preact';
import { signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { locationState, navigate } from '../../app/router.js';
import { hasCurrentPermissionFlag, navigationItemsState } from '../../data/metadata-store.js';
import { Icon } from '../../ui/icon.js';
import { useIsMobile } from '../../ui/hooks.js';
import { openCommandMenu, sidePanelState } from '../side-panel/side-panel-state.js';
import { unreadConversationCountState } from '../conversations/conversations-state.js';
import { isMobileNavigationOpenState } from './navigation-state.js';
import { t } from '../../i18n/index.js';
import { hasBottomComposer } from '../../lib/navigation-visibility.js';

// rolagem acumulada que esconde ou mostra a barra
const SCROLL_THRESHOLD = 24;

// estado de rolagem único para a barra e o cartão das novidades, que somem e voltam juntos
const isHiddenByScrollState = signal(false);
let scrollSubscriberCount = 0;
/** @type {(() => void) | null} */
let detachScrollListener = null;

function attachScrollListener() {
  let accumulated = 0;
  const lastTops = new WeakMap();
  /** @param {Event} event */
  const handleScroll = (event) => {
    const target = /** @type {HTMLElement} */ (event.target instanceof Document ? document.scrollingElement : event.target);
    if (!target || typeof target.scrollTop !== 'number') return;
    const previous = lastTops.get(target) ?? target.scrollTop;
    const delta = target.scrollTop - previous;
    lastTops.set(target, target.scrollTop);
    if (target.scrollTop <= 8) {
      accumulated = 0;
      isHiddenByScrollState.value = false;
      return;
    }
    // mudou de direção: começa a contar de novo
    if (Math.sign(delta) !== Math.sign(accumulated)) accumulated = 0;
    accumulated += delta;
    if (accumulated > SCROLL_THRESHOLD) isHiddenByScrollState.value = true;
    else if (accumulated < -SCROLL_THRESHOLD) isHiddenByScrollState.value = false;
  };
  document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
  return () => document.removeEventListener('scroll', handleScroll, { capture: true });
}

function useHideOnScroll() {
  useEffect(() => {
    scrollSubscriberCount += 1;
    if (scrollSubscriberCount === 1) detachScrollListener = attachScrollListener();
    return () => {
      scrollSubscriberCount -= 1;
      if (scrollSubscriberCount > 0) return;
      detachScrollListener?.();
      detachScrollListener = null;
      isHiddenByScrollState.value = false;
    };
  }, []);
  return isHiddenByScrollState.value;
}

/**
 * Barra flutuante e o que fica acima dela (cartão das novidades) somem ao rolar para baixo, com uma conversa, um
 * atendimento ou uma consulta aberta (o campo de mensagem fica no rodapé), com o painel lateral e com o menu do celular
 * abertos.
 */
export function useIsMobileBottomChromeHidden() {
  const isHiddenByScroll = useHideOnScroll();
  const pathname = locationState.value.pathname;
  const isConversationOpen = hasBottomComposer(pathname);
  return isHiddenByScroll || isConversationOpen || Boolean(sidePanelState.value) || isMobileNavigationOpenState.value;
}

export function MobileNavigationBar() {
  const isMobile = useIsMobile();
  const isHidden = useIsMobileBottomChromeHidden();
  const pathname = locationState.value.pathname;
  if (!isMobile) return null;
  const hasVisits = navigationItemsState.value.some((item) => item.href === '/visits');
  const hasConversations = hasCurrentPermissionFlag('CONVERSATIONS');
  const unreadCount = unreadConversationCountState.value;
  /** @type {Array<{ key: string, icon: string, label: string, isActive: boolean, badge?: number, onClick: () => void }>} */
  const items = [
    { key: 'menu', icon: 'IconMenu2', label: t('navigationMobile.mobileBar.menu'), isActive: false, onClick: () => (isMobileNavigationOpenState.value = true) },
    { key: 'search', icon: 'IconSearch', label: t('navigationMobile.mobileBar.search'), isActive: false, onClick: openCommandMenu },
  ];
  if (hasConversations) {
    items.push({
      key: 'conversations', icon: 'IconMessages', label: t('navigationMobile.mobileBar.conversations'), badge: unreadCount,
      isActive: pathname.startsWith('/conversations'), onClick: () => navigate('/conversations'),
    });
  }
  if (hasVisits) {
    items.push({ key: 'visits', icon: 'IconDoorEnter', label: t('navigationMobile.mobileBar.visits'), isActive: pathname.startsWith('/visits'), onClick: () => navigate('/visits') });
  }
  return html`<div class="mobile-navigation-bar-container" data-hidden=${isHidden ? 'true' : undefined}>
    <nav class="mobile-navigation-bar" aria-label=${t('navigationMobile.mobileBar.label')} aria-hidden=${isHidden ? 'true' : undefined}>
      ${items.map((item) => html`<button
        key=${item.key}
        type="button"
        class="mobile-navigation-bar-item"
        aria-label=${item.label}
        title=${item.label}
        aria-pressed=${item.isActive ? 'true' : 'false'}
        data-active=${item.isActive ? 'true' : undefined}
        tabindex=${isHidden ? -1 : undefined}
        onClick=${item.onClick}
      >
        <${Icon} name=${item.icon} size=${20} />
        ${item.badge ? html`<span class="mobile-navigation-bar-badge">${item.badge > 99 ? '99+' : item.badge}</span>` : null}
      </button>`)}
    </nav>
  </div>`;
}
